import clsx, { ClassValue } from 'clsx';
import dayjs from 'dayjs';

export const APP_DATE_FORMAT = 'DD/MM/YYYY';
export const APP_DATETIME_FORMAT = 'DD/MM/YYYY hh:mm A';

/** e.g. GMT+3 or GMT+5:30 — based on the viewer's timezone. */
export function getAppGmtOffsetLabel(forDate: Date = new Date()): string {
  const offsetMinutes = -forDate.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  if (mins === 0) return `GMT${sign}${hours}`;
  return `GMT${sign}${hours}:${String(mins).padStart(2, '0')}`;
}

/** Parse API timestamps; treat ISO strings without a zone as UTC (.NET default). */
function parseAppDateTime(value: string | Date): dayjs.Dayjs {
  if (value instanceof Date) return dayjs(value);
  const trimmed = value.trim();
  if (!trimmed) return dayjs(Number.NaN);
  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const hasTimezone =
      /Z$/i.test(trimmed) ||
      /[+-]\d{2}:\d{2}$/.test(trimmed) ||
      /[+-]\d{4}$/.test(trimmed);
    if (!hasTimezone) return dayjs(new Date(`${trimmed}Z`));
  }
  return dayjs(trimmed);
}

export const cn = (...classes: ClassValue[]) => {
  return clsx(classes);
};

/** Short local timezone label for a date (e.g. GMT, EST, EEST, GMT+3). */
export function getLocalTimeZoneAbbreviation(
  value?: string | Date | null
): string {
  const date =
    value == null || value === ''
      ? new Date()
      : value instanceof Date
        ? value
        : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(date);
    const label = parts.find((part) => part.type === 'timeZoneName')?.value?.trim();
    if (label) return label;
  } catch {
    // Fall through to GMT offset.
  }

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return minutes === 0
    ? `GMT${sign}${hours}`
    : `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

export function formatAppDate(value?: string | Date | null): string {
  if (value == null || value === '') return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(APP_DATE_FORMAT) : '—';
}

export function formatAppDateTime(value?: string | Date | null): string {
  if (value == null || value === '') return '—';
  const parsed = parseAppDateTime(value);
  if (!parsed.isValid()) return '—';
  return parsed.format(APP_DATETIME_FORMAT);
}

export function formatAppDateRange(
  from?: string | Date | null,
  to?: string | Date | null
): string {
  const fromDate = from ? dayjs(from) : null;
  const toDate = to ? dayjs(to) : null;
  if (!fromDate?.isValid() || !toDate?.isValid()) return '—';
  if (fromDate.isSame(toDate, 'day')) return fromDate.format(APP_DATE_FORMAT);
  return `${fromDate.format(APP_DATE_FORMAT)} – ${toDate.format(APP_DATE_FORMAT)}`;
}

export const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  if (!options) {
    return formatAppDate(date);
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj);
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const truncate = (str: string, length: number, suffix: string = '...'): string => {
  if (str.length <= length) return str;
  return str.substring(0, length) + suffix;
};

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const isEmpty = (obj: any): boolean => {
  if (obj === null || obj === undefined) return true;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
};

export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const parseQueryString = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

/** Normalize API/axios rejection objects into a user-visible message. */
export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again."
): string {
  if (!error) return fallback;
  if (typeof error === "string") {
    const trimmed = error.trim();
    return trimmed || fallback;
  }
  if (error instanceof Error && error.message?.trim()) {
    return error.message.trim();
  }
  if (typeof error === "object" && error !== null && "message" in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg.trim();
  }
  return fallback;
}

